/**
 * Transactions API — the ledger surface at /api/finance/transactions.
 *
 * `listTransactions` defaults to accepted rows only (the server enforces
 * this); pass `review_state: null` explicitly to see everything.
 */

import {
  apiClient,
  buildQueryString,
  type PaginatedResponse,
} from "@/shared/lib/api/client";
import type {
  Transaction,
  TransactionCreateInput,
  TransactionEvent,
  TransactionFilters,
  TransactionUpdateInput,
} from "../types";

const BASE = "/api/finance/transactions";

export function listTransactions(
  filters?: TransactionFilters,
): Promise<PaginatedResponse<Transaction>> {
  const qs = buildQueryString({
    account_id: filters?.account_id,
    category_id: filters?.category_id,
    merchant_id: filters?.merchant_id,
    status: filters?.status,
    // null means "all review states" — sent as the literal string the
    // backend treats as the wildcard; undefined omits the param entirely
    // (server default: accepted only).
    review_state:
      filters?.review_state === null
        ? "all"
        : filters?.review_state,
    include_transfers: filters?.include_transfers,
    since: filters?.since,
    until: filters?.until,
    search: filters?.search,
    skip: filters?.skip ?? 0,
    limit: filters?.limit ?? 100,
  });
  return apiClient<PaginatedResponse<Transaction>>(`${BASE}${qs}`);
}

export function getTransaction(transactionId: string): Promise<Transaction> {
  return apiClient<Transaction>(`${BASE}/${transactionId}`);
}

export function createTransaction(
  data: TransactionCreateInput,
): Promise<Transaction> {
  return apiClient<Transaction>(BASE, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateTransaction(
  transactionId: string,
  data: TransactionUpdateInput,
): Promise<Transaction> {
  return apiClient<Transaction>(`${BASE}/${transactionId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/** The evidence trail: every assertion any source ever made about a row. */
export function listTransactionEvents(
  transactionId: string,
): Promise<TransactionEvent[]> {
  return apiClient<TransactionEvent[]>(`${BASE}/${transactionId}/events`);
}
